import { Component, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TranslateModule } from '@ngx-translate/core';

export type DynamicFieldType = 'text' | 'textarea' | 'number' | 'select' | 'autocomplete' | 'toggle' | 'file' | 'color';

export interface DynamicDialogOption {
  label: string;
  value: string | number | boolean;
}

export interface DynamicDialogField {
  key: string;
  label: string;
  type: DynamicFieldType;
  required?: boolean;
  disabled?: boolean;
  disabledWhen?: (formValue: Record<string, unknown>) => boolean;
  asciiOnly?: boolean;
  uppercase?: boolean;
  allowUnicode?: boolean;
  preserveCase?: boolean;
  multiple?: boolean;
  placeholder?: string;
  hint?: string;
  rows?: number;
  options?: DynamicDialogOption[];
  optionsResolver?: (formValue: Record<string, unknown>) => DynamicDialogOption[];
  searchResolver?: (
    search: string,
    formValue: Record<string, unknown>
  ) => Promise<DynamicDialogOption[]> | DynamicDialogOption[];
  displayValueResolver?: (value: unknown, formValue: Record<string, unknown>) => string;
  searchDebounceMs?: number;
  fullWidth?: boolean;
  visibleWhen?: (formValue: Record<string, unknown>) => boolean;
  accept?: string;
  minLength?: number;
}

export interface DynamicFormDialogData<T = Record<string, unknown>> {
  action: 'add' | 'edit';
  title: string;
  submitLabel?: string;
  value?: Partial<T>;
  fields: DynamicDialogField[];
  submitHandler?: (value: T) => boolean | void | Promise<boolean | void>;
}

@Component({
  selector: 'app-form-dialog',
  standalone: true,
  templateUrl: './form-dialog.component.html',
  styleUrls: ['./form-dialog.component.scss'],
  imports: [
    MatButtonModule,
    MatDialogContent,
    MatDialogClose,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class FormDialogComponent {
  private fb = inject(FormBuilder);
  readonly dialogRef = inject(MatDialogRef<FormDialogComponent>);
  readonly data = inject<DynamicFormDialogData>(MAT_DIALOG_DATA);

  readonly form = this.buildForm();
  selectSearch: Record<string, string> = {};
  autocompleteSearch: Record<string, string> = {};
  autocompleteOptions: Record<string, DynamicDialogOption[]> = {};
  autocompleteLoading: Record<string, boolean> = {};
  filePreviews: Record<string, string> = {};
  isSubmitting = false;
  private autocompleteDebounceHandles: Record<string, ReturnType<typeof setTimeout> | null> = {};
  private autocompleteSelectionInProgress: Record<string, boolean> = {};

  constructor() {
    this.initializeFilePreviews();
    this.initializeAutocompleteFields();
    this.form.valueChanges.subscribe(() => this.syncDynamicFields());
    this.syncDynamicFields();
  }

  private buildForm(): FormGroup {
    const controls = this.data.fields.reduce<Record<string, FormControl>>((acc, field) => {
      const initialValue = this.data.value?.[field.key as keyof typeof this.data.value] ?? this.getDefaultValue(field);
      const validators = field.required ? [Validators.required] : [];
      if (typeof field.minLength === 'number') {
        validators.push(Validators.minLength(field.minLength));
      }
      if (field.type === 'color') {
        validators.push(Validators.pattern(/^#[0-9a-fA-F]{6}$/));
      }

      acc[field.key] = this.fb.control(
        { value: initialValue, disabled: !!field.disabled },
        { nonNullable: false, validators }
      ) as FormControl;

      return acc;
    }, {});

    return this.fb.group(controls);
  }

  private getDefaultValue(field: DynamicDialogField): string | number | boolean | null {
    if (field.type === 'toggle') {
      return false;
    }

    return null;
  }

  private initializeFilePreviews(): void {
    this.data.fields
      .filter((field) => field.type === 'file')
      .forEach((field) => {
        const initialValue = this.data.value?.[field.key as keyof typeof this.data.value];
        if (typeof initialValue === 'string' && initialValue.trim()) {
          this.filePreviews[field.key] = initialValue.trim();
        }
      });
  }

  private initializeAutocompleteFields(): void {
    this.data.fields
      .filter((field) => field.type === 'autocomplete')
      .forEach((field) => {
        const controlValue = this.form.get(field.key)?.value;
        this.autocompleteSearch[field.key] = this.resolveAutocompleteDisplayValue(field, controlValue);
      });
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = {
      ...this.data.value,
      ...this.form.getRawValue(),
    } as Record<string, unknown>;

    if (!this.data.submitHandler) {
      this.dialogRef.close(value);
      return;
    }

    this.isSubmitting = true;
    try {
      const shouldClose = await this.data.submitHandler(value);
      if (shouldClose !== false) {
        this.dialogRef.close(value);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  hasRequiredError(fieldKey: string): boolean {
    const control = this.form.get(fieldKey);
    return !!control && control.hasError('required') && (control.touched || control.dirty);
  }

  hasMinLengthError(field: DynamicDialogField): boolean {
    const control = this.form.get(field.key);
    return !!control && control.hasError('minlength') && (control.touched || control.dirty);
  }

  getFilteredOptions(field: DynamicDialogField): DynamicDialogOption[] {
    const formValue = this.form.getRawValue() as Record<string, unknown>;
    const options = field.optionsResolver ? field.optionsResolver(formValue) : (field.options || []);
    const filter = (this.selectSearch[field.key] || '').trim().toLowerCase();

    if (!filter) {
      return options;
    }

    return options.filter((option) =>
      String(option.label).toLowerCase().includes(filter) ||
      String(option.value).toLowerCase().includes(filter)
    );
  }

  shouldShowField(field: DynamicDialogField): boolean {
    const formValue = this.form.getRawValue() as Record<string, unknown>;
    return field.visibleWhen ? field.visibleWhen(formValue) : true;
  }

  resetSelectSearch(fieldKey: string): void {
    this.selectSearch[fieldKey] = '';
  }

  handleSelectOpenedChange(fieldKey: string, opened: boolean): void {
    void opened;
    this.resetSelectSearch(fieldKey);
  }

  getAutocompleteOptions(fieldKey: string): DynamicDialogOption[] {
    return this.autocompleteOptions[fieldKey] || [];
  }

  getAutocompleteSearchValue(fieldKey: string): string {
    return this.autocompleteSearch[fieldKey] || '';
  }

  isAutocompleteLoading(fieldKey: string): boolean {
    return !!this.autocompleteLoading[fieldKey];
  }

  async openAutocomplete(field: DynamicDialogField): Promise<void> {
    await this.loadAutocompleteOptions(field, this.getAutocompleteSearchValue(field.key));
  }

  handleAutocompleteInput(field: DynamicDialogField, value: string): void {
    this.autocompleteSearch[field.key] = value;

    if (this.autocompleteSelectionInProgress[field.key]) {
      return;
    }

    this.form.get(field.key)?.setValue(null, { emitEvent: false });

    const debounceMs = field.searchDebounceMs ?? 250;
    if (this.autocompleteDebounceHandles[field.key]) {
      clearTimeout(this.autocompleteDebounceHandles[field.key] as ReturnType<typeof setTimeout>);
    }

    this.autocompleteDebounceHandles[field.key] = setTimeout(() => {
      void this.loadAutocompleteOptions(field, value);
    }, debounceMs);
  }

  handleAutocompleteSelected(field: DynamicDialogField, option: DynamicDialogOption): void {
    this.autocompleteSelectionInProgress[field.key] = true;
    this.form.get(field.key)?.setValue(option.value);
    this.autocompleteSearch[field.key] = option.label;

    setTimeout(() => {
      this.autocompleteSelectionInProgress[field.key] = false;
    });
  }

  handleFileSelected(fieldKey: string, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] || null;
    const control = this.form.get(fieldKey);

    if (!control) {
      return;
    }

    control.setValue(file);

    if (!file) {
      delete this.filePreviews[fieldKey];
      return;
    }

    if (!file.type.startsWith('image/')) {
      delete this.filePreviews[fieldKey];
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (result) {
        this.filePreviews[fieldKey] = result;
      }
    };
    reader.readAsDataURL(file);
  }

  getFilePreview(fieldKey: string): string {
    return this.filePreviews[fieldKey] || '';
  }

  getSelectedFileName(fieldKey: string): string {
    const value = this.form.get(fieldKey)?.value;
    if (value instanceof File) {
      return value.name;
    }

    return '';
  }

  sanitizeTextInput(field: DynamicDialogField, event: Event): void {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    const control = this.form.get(field.key);
    if (!input || !control) {
      return;
    }

    let sanitizedValue = input.value;
    if (field.asciiOnly) {
      sanitizedValue = this.toAscii(sanitizedValue);
    }
    if (field.uppercase) {
      sanitizedValue = sanitizedValue.toUpperCase();
    }

    if (input.value !== sanitizedValue) {
      input.value = sanitizedValue;
      control.setValue(sanitizedValue);
    }
  }

  private toAscii(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\x20-\x7E]/g, '');
  }

  private async loadAutocompleteOptions(field: DynamicDialogField, search: string): Promise<void> {
    const formValue = this.form.getRawValue() as Record<string, unknown>;
    this.autocompleteLoading[field.key] = true;
    try {
      const baseOptions = field.searchResolver
        ? await Promise.resolve(field.searchResolver(search, formValue))
        : (field.optionsResolver ? field.optionsResolver(formValue) : (field.options || []));
      this.autocompleteOptions[field.key] = Array.isArray(baseOptions) ? baseOptions : [];
    } finally {
      this.autocompleteLoading[field.key] = false;
    }
  }

  private resolveAutocompleteDisplayValue(field: DynamicDialogField, value: unknown): string {
    const formValue = this.form.getRawValue() as Record<string, unknown>;
    if (field.displayValueResolver) {
      return field.displayValueResolver(value, formValue);
    }

    const options = field.optionsResolver ? field.optionsResolver(formValue) : (field.options || []);
    return options.find((option) => option.value === value)?.label || '';
  }

  private syncDynamicFields(): void {
    const formValue = this.form.getRawValue() as Record<string, unknown>;

    this.data.fields.forEach((field) => {
      const control = this.form.get(field.key);
      if (!control) {
        return;
      }

      const isVisible = field.visibleWhen ? field.visibleWhen(formValue) : true;
      if (!isVisible) {
        if (control.enabled) {
          control.disable({ emitEvent: false });
        }
        const hiddenValue = field.multiple ? [] : null;
        if (JSON.stringify(control.value) !== JSON.stringify(hiddenValue)) {
          control.setValue(hiddenValue, { emitEvent: false });
        }
        return;
      }

      const shouldDisable = field.disabled || (field.disabledWhen ? field.disabledWhen(formValue) : false);
      if (shouldDisable && control.enabled) {
        control.disable({ emitEvent: false });
      } else if (!shouldDisable && control.disabled) {
        control.enable({ emitEvent: false });
      }

      if (field.type !== 'select') {
        if (field.type === 'autocomplete') {
          this.autocompleteSearch[field.key] = this.resolveAutocompleteDisplayValue(field, control.value);
        }
        return;
      }

      const options = field.optionsResolver ? field.optionsResolver(formValue) : (field.options || []);
      const optionValues = new Set(options.map((option) => option.value));
      const currentValue = control.value;

      if (field.multiple) {
        const normalizedValue = Array.isArray(currentValue)
          ? currentValue.filter((value) => optionValues.has(value))
          : [];

        if (JSON.stringify(currentValue) !== JSON.stringify(normalizedValue)) {
          control.setValue(normalizedValue, { emitEvent: false });
        }
        return;
      }

      if (currentValue == null || currentValue === '' || optionValues.has(currentValue)) {
        return;
      }

      control.setValue(options.length ? options[0].value : null, { emitEvent: false });
    });
  }
}
